// 邻船 (seamless boats): the people at the other boat's 跳板 / 码头, drawn where they stand seen from here (on the
// moored ship the raft's map paints, or on the raft's corner south of the 活动舱). The office forwards them
// (src/office/edge.ts): {t:"nb", on, g:[…]} with their positions already in this map's frame (public/deck/moor.js), a
// look only the first time; {t:"nb_to", g, id} when one of them walked across and became the otter `id` here (it glides
// on from where its ghost was). A ghost's `from`: the otter that just left this boat for that one (it glides on from
// where that otter was). Ghosts are drawn like anyone (deck/scene.js, through view.peers) but are not in the page's
// peers: no card, no greeting, no 双人互动, nothing to click.
// Standing at this boat's edge also warms the other boat's map in the browser's cache, so walking across loads fast.
// No imports (app.js hands it what it needs: one module instance each).

const LEFT_MS = 30_000;

/** o: { MAP, me(), peers() (the page's Map), floor (initFloors' result), setEmote (emotes.js), inEdge / portalTo
 *  (deck/moor.js), drop(q) (let go of a ghost's pulled mic: app.js dropMedia) } */
export function initNeighbours({ MAP, me, peers, floor, setEmote, inEdge, portalTo, drop = () => {} }) {
  /** id ("nb:…") → the ghost, shaped like a peer (drawX/drawY for the scene). */
  const ghosts = new Map();
  /** Otters that left this boat lately, and ghosts gone lately (they may have walked across): id → where they were drawn. */
  const left = new Map(), vanished = new Map();
  const keep = (map, id, q) => {
    const now = performance.now();
    for (const [k, v] of map) if (now - v.at > LEFT_MS) map.delete(k);
    map.set(id, { x: q.drawX ?? q.x, y: q.drawY ?? q.y, at: now });
  };

  function msg(m) {
    if (m.t === 'nb' && Array.isArray(m.g)) {
      const seen = new Set();
      for (const g of m.g) {
        if (!g || typeof g.id !== 'string' || !g.id.startsWith('nb:') || !Number.isFinite(g.x) || !Number.isFinite(g.y)) continue;
        seen.add(g.id);
        let q = ghosts.get(g.id);
        if (!q) {
          const from = typeof g.from === 'string' ? left.get(g.from) : null;
          q = { id: g.id, ghost: true, look: null, seed: g.id, emote: null, drawX: from?.x ?? g.x, drawY: from?.y ?? g.y };
          if (from) left.delete(g.from);
          ghosts.set(g.id, q);
        }
        const was = q.emoteId;
        // their mic (邻船 voice: the office lets me pull it while I hear them, sfu.ts); a new one: let go of the old pull
        const sid = typeof g.sessionId === 'string' ? g.sessionId : null, track = typeof g.trackName === 'string' ? g.trackName : null;
        if (q.mid && !q.closing && (q.sessionId !== sid || q.trackName !== track)) drop(q);
        q.sessionId = sid; q.trackName = track;
        Object.assign(q, { name: String(g.name ?? ''), kind: g.kind === 'agent' ? 'agent' : 'human', x: g.x, y: g.y, dir: g.dir ?? 0, dir8: g.dir8, dir16: g.dir16,
          moving: !!g.moving, boat: !!g.boat, muted: !!g.muted, status: g.status ?? null });
        if (g.look !== undefined) q.look = g.look;
        q.emoteId = g.emote?.emote ?? 0;
        if (q.emoteId !== was) setEmote(q, q.emoteId, g.emote?.ms ?? 0);
      }
      for (const [id, q] of [...ghosts]) if (!seen.has(id)) { keep(vanished, id, q); drop(q); ghosts.delete(id); }
    } else if (m.t === 'nb_to') {
      const g = ghosts.get(m.g) ?? vanished.get(m.g), p = peers().get(m.id);
      if (g && p) { p.drawX = g.drawX ?? g.x; p.drawY = g.drawY ?? g.y; }
      if (ghosts.has(m.g)) drop(ghosts.get(m.g));
      ghosts.delete(m.g); vanished.delete(m.g);
    }
  }
  /** An otter left this boat (the office's "leave"): kept a while, in case its ghost turns up on the other boat. */
  function gone(p) {
    if (p) keep(left, p.id, p);
  }

  // the other boat's map, fetched once while I stand at the edge (the browser keeps it for the walk across)
  const to = floor?.kind === 'play' ? 'main' : 'play', portal = portalTo(MAP, to);
  let warmed = !portal || !floor?.room;
  const timer = setInterval(() => {
    const m = me(); if (warmed || !m || !inEdge(portal, m.x, m.y)) return;
    warmed = true;
    fetch(to === 'play' ? '/play.tmj' : '/map.tmj').then((r) => r.json()).then((j) => {
      for (const ts of j.tilesets ?? []) if (typeof ts.image === 'string') { const i = new Image(); i.src = '/' + ts.image.replace(/^\.?\//, ''); }
    }).catch(() => {});
  }, 1000);

  return {
    ghosts, msg, gone,
    /** What the scene draws: the page's peers and, when there are any, the ghosts. */
    all: () => (ghosts.size ? new Map([...peers(), ...ghosts]) : peers()),
    clear: () => { for (const q of ghosts.values()) drop(q); ghosts.clear(); },
    stop: () => clearInterval(timer),
  };
}
